import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { ScrambleText } from "@/components/ScrambleText";
import { Countdown } from "@/components/Countdown";

const MILESTONES = [
  {
    name: "Take It Down Act",
    status: "In Force",
    pending: false,
    since: Date.UTC(2026, 4, 19),
    label: "days in force",
    line: "48-hour deepfake removal · FTC enforced · $53,088 per violation",
    meaning: "Provenance turns a 48-hour removal scramble into a signed record.",
    source: "https://www.congress.gov/119/plaws/publ12/PLAW-119publ12.htm",
    testId: "ledger-take-it-down",
  },
  {
    name: "No Fakes Act",
    status: "Pending",
    pending: true,
    since: Date.UTC(2025, 3, 9),
    label: "days pending",
    line: "Federal right over digital voice replicas · awaiting floor vote",
    meaning: "If passed, every synthetic voice requires traceable origin — the proof this device mints.",
    source: "https://www.congress.gov/bill/119th-congress/senate-bill/1367",
    testId: "ledger-no-fakes",
  },
];

const daysSince = (ts) => Math.max(0, Math.floor((Date.now() - ts) / 86400000));

const LedgerCell = ({ m }) => {
  const [days, setDays] = useState(() => daysSince(m.since));

  useEffect(() => {
    const t = setInterval(() => setDays(daysSince(m.since)), 60000);
    return () => clearInterval(t);
  }, [m.since]);

  return (
    <div
      data-testid={m.testId}
      className="group border border-transparent bg-[#0C0E15] p-8 transition-colors duration-500 hover:border-[#C4A24C] sm:p-10"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-serif text-lg text-[#C4A24C]">{m.name}</span>
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
          <span className={`h-1.5 w-1.5 ${m.pending ? "led-blink" : "bg-[#C4A24C]"}`} aria-hidden="true" />
          {m.status}
        </span>
      </div>
      <div className="mt-6 flex items-baseline gap-3">
        <span data-testid={`${m.testId}-days`} className="font-serif text-4xl tabular-nums text-[#C4A24C]">
          {days}
        </span>
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">{m.label}</span>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-[#E4E5EA]/60">{m.line}</p>
      <div className="mt-0 max-h-0 overflow-hidden opacity-0 transition-all duration-500 group-hover:mt-4 group-hover:max-h-40 group-hover:opacity-100">
        <p className="border-t border-white/5 pt-4 text-xs italic leading-relaxed text-[#E4E5EA]/80">
          {m.meaning}
        </p>
        <a
          href={m.source}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${m.testId}-source`}
          className="mt-3 inline-block text-[10px] uppercase tracking-[0.3em] text-[#6B7280] transition-colors duration-300 hover:text-[#C4A24C]"
        >
          Source ↗
        </a>
      </div>
    </div>
  );
};

export default function Regulatory() {
  return (
    <section id="regulatory" data-testid="regulatory-section" className="px-6 py-28 sm:px-10 lg:px-20 lg:py-36">
      <Reveal>
        <div
          data-testid="regulatory-banner"
          className="mx-auto flex max-w-7xl flex-col justify-between gap-12 border-l-2 border-[#C4A24C] bg-[#0C0E15] px-6 py-10 sm:px-10 lg:flex-row lg:items-center lg:px-16 lg:py-20"
        >
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.35em] text-[#C4A24C]">
              <ScrambleText text="05 / The Regulatory Moment" />
            </p>
            <h2 className="mt-8 font-serif text-4xl text-[#C4A24C] sm:text-5xl">
              December 2, 2026.
            </h2>
            <p className="mt-8 text-sm leading-relaxed text-[#E4E5EA]/90 sm:text-base">
              EU AI Act Article 50 is in force. Machine-readable watermarking is now mandatory.
              Non-compliance: up to €15M or 3% of worldwide revenue.
            </p>
          </div>
          <Countdown />
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          data-testid="regulatory-ledger"
          className="mx-auto mt-6 grid max-w-7xl grid-cols-1 divide-y divide-white/5 border border-white/5 sm:grid-cols-2 sm:divide-x sm:divide-y-0"
        >
          {MILESTONES.map((m) => (
            <LedgerCell key={m.name} m={m} />
          ))}
        </div>
      </Reveal>
    </section>
  );
}
